import { useState, useEffect } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import { useNavigate } from 'react-router-dom'
import { Users, X, ChevronDown, ChevronUp, Plus, HelpCircle, Search, Check } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useProfileStore } from '../../stores'
import ProfileSubpageLayout from '../../components/ProfileSubpageLayout'
import { FAMILY_CONDITIONS } from '../../data/medicalLists'
// TEST 1.03 — the ONE coercion; see src/utils/profileConditions.js. `if (x.conditions)`
// is truthy for the object the import wrote, and .forEach then threw.
import { toConditionList } from '../../utils/profileConditions'

export default function ProfileFamilyHistory() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { profile, updateProfile, syncSection } = useProfileStore()
  
  // Initialize conditions from FAMILY_CONDITIONS with relative flags
  const buildDefaults = () => FAMILY_CONDITIONS.map(c => ({ ...c, mother: false, father: false, sibling: false, grandparent: false }))

  const [conditions, setConditions] = useState(buildDefaults)
  const [otherConditions, setOtherConditions] = useState([])
  const [unknownRelatives, setUnknownRelatives] = useState({ mother: false, father: false, sibling: false })
  const [showAddModal, setShowAddModal] = useState(false)
  const [newCondition, setNewCondition] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (profile?.familyHistory) {
      if (profile.familyHistory.conditions) {
        const savedMap = {}
        toConditionList(profile.familyHistory.conditions).forEach(c => { savedMap[c.id] = c })
        // Merge saved data into comprehensive list
        setConditions(buildDefaults().map(dc => savedMap[dc.id] ? { ...dc, ...savedMap[dc.id] } : dc))
      }
      if (profile.familyHistory.otherConditions) setOtherConditions(toConditionList(profile.familyHistory.otherConditions))
      if (profile.familyHistory.unknownRelatives) setUnknownRelatives(profile.familyHistory.unknownRelatives)
    }
  }, [profile])

  const handleSave = async () => {
    const data = { conditions, otherConditions, unknownRelatives, completed: true }
    const result = await syncSection('familyHistory', data)
    if (!result.success) updateProfile({ familyHistory: data })
    navigate('/profile')
  }

  const hasAnyRelative = (c) => c.mother || c.father || c.sibling || c.grandparent
  const handleToggleUnknown = (relative) => { setUnknownRelatives({ ...unknownRelatives, [relative]: !unknownRelatives[relative] }) }
  const toggleRelative = (conditionId, relative) => {
    setConditions(conditions.map(c => c.id === conditionId ? { ...c, [relative]: !c[relative] } : c))
  }
  const toggleOtherRelative = (index, relative) => {
    const updated = [...otherConditions]; updated[index] = { ...updated[index], [relative]: !updated[index][relative] }; setOtherConditions(updated)
  }
  const handleAddCondition = () => {
    if (newCondition.trim()) {
      const newItem = { id: `custom-${Date.now()}`, name: newCondition.trim(), mother: false, father: false, sibling: false, grandparent: false }
      setOtherConditions([...otherConditions, newItem])
      setExpandedId(newItem.id); setNewCondition(''); setShowAddModal(false)
    }
  }
  const handleRemoveOther = (index) => { setOtherConditions(otherConditions.filter((_, i) => i !== index)) }

  // Search filtering
  const normalizedQuery = query.toLowerCase().trim()
  const activeConditions = conditions.filter(c => hasAnyRelative(c))
  const inactiveConditions = conditions.filter(c => !hasAnyRelative(c))

  // What to show when not searching: only common inactive items
  const commonInactive = inactiveConditions.filter(c => c.common)
  // What to show when searching: all matching inactive items
  const searchInactive = normalizedQuery
    ? inactiveConditions.filter(c => c.name.toLowerCase().includes(normalizedQuery) || (c.category && c.category.toLowerCase().includes(normalizedQuery)))
    : []

  const RelativeButtons = ({ condition, onToggle }) => (
    <div className="flex gap-2">
      {['mother', 'father', 'sibling', 'grandparent'].map((relative) => (
        <button key={relative} onClick={() => onToggle(relative)}
          className={`flex-1 py-2 rounded-[14px] text-[11px] font-semibold transition-all ${condition[relative] ? 'bg-[#46A8F9] text-white' : 'bg-gray-50 border-[1.5px] border-gray-200 text-gray-500'}`}>
          {relative === 'grandparent' ? 'GP' : relative.charAt(0).toUpperCase() + relative.slice(1)}
          {unknownRelatives[relative] && ' (?)'}
        </button>
      ))}
    </div>
  )

  const renderConditionCard = (condition, isOther = false, otherIndex = -1) => {
    const isActive = hasAnyRelative(condition)
    const isExpanded = expandedId === condition.id
    return (
      <div key={condition.id} className={`rounded-[14px] border-[1.5px] overflow-hidden transition-all ${isActive ? 'border-[#46A8F9] bg-[#E9F8FF]' : 'border-gray-200'}`}>
        <button onClick={() => setExpandedId(isExpanded ? null : condition.id)} className="w-full px-3.5 py-2.5 flex items-center justify-between">
          <span className={`text-[13px] ${isActive ? 'font-semibold' : 'font-medium text-gray-600'}`} style={isActive ? { color: '#15354F' } : {}}>{isOther ? condition.name : t('familyConditions.' + condition.id, { defaultValue: condition.name })}</span>
          <div className="flex items-center gap-2">
            {isActive && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: '#E9F8FF', color: '#46A8F9' }}>
                {[condition.mother && 'M', condition.father && 'F', condition.sibling && 'S', condition.grandparent && 'GP'].filter(Boolean).join(', ')}
              </span>
            )}
            {isOther && <button onClick={(e) => { e.stopPropagation(); handleRemoveOther(otherIndex) }}><X size={14} className="text-red-400" /></button>}
            {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </div>
        </button>
        {isExpanded && (
          <div className="px-3.5 pb-3 border-t border-blue-100">
            <p className="text-[11px] text-gray-500 mt-2 mb-2">{t('profileFamily.whichFamilyMembers')}</p>
            <RelativeButtons condition={condition} onToggle={(relative) => isOther ? toggleOtherRelative(otherIndex, relative) : toggleRelative(condition.id, relative)} />
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <ProfileSubpageLayout sectionId="family-history" icon={Users} onSave={handleSave} saving={false} canSave={true}>
        {/* Unknown History */}
        <div className="mb-5 p-4 rounded-[14px] border-[1.5px] border-amber-200 bg-amber-50/50">
          <div className="flex items-center gap-2 mb-2">
            <HelpCircle size={16} className="text-amber-600" />
            <p className="text-[13px] font-bold text-amber-800">{t('profileFamily.unknownFamilyHistory')}</p>
          </div>
          <p className="text-[12px] text-amber-700 mb-3">{t('profileFamily.markAnyRelativesWhoseMedical')}</p>
          <div className="flex gap-2">
            {['mother', 'father', 'sibling', 'grandparent'].map((relative) => (
              <button key={relative} onClick={() => handleToggleUnknown(relative)}
                className={`flex-1 py-2 px-2 rounded-[14px] text-[11px] font-semibold transition-all ${unknownRelatives[relative] ? 'bg-amber-500 text-white' : 'bg-white border-[1.5px] border-amber-300 text-amber-700'}`}>
                {t('profileFamilyHistory.' + relative)}
              </button>
            ))}
          </div>
        </div>

        <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileFamily.hereditaryConditions')}</p>

        {/* Search — always at top */}
        <div className="relative mb-3">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('profileFamilyHistory.searchConditions')}
            className="w-full pl-10 pr-10 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9]" style={{ color: '#15354F' }} />
          {query && <button onClick={() => setQuery('')} className="absolute right-3.5 top-1/2 -translate-y-1/2"><X size={16} className="text-gray-400" /></button>}
        </div>

        {/* Search results — only when searching */}
        {normalizedQuery && (
          <div className="space-y-1.5 mb-3">
            {searchInactive.length > 0 ? (
              searchInactive.map(c => renderConditionCard(c))
            ) : activeConditions.filter(c => c.name.toLowerCase().includes(normalizedQuery)).length === 0 ? (
              <p className="text-[12px] text-gray-400 text-center py-3">{t('profileFamilyHistory.noMatches', { query })}</p>
            ) : null}
            <p className="text-[10px] text-gray-300 text-center">
              {searchInactive.length + activeConditions.filter(c => c.name.toLowerCase().includes(normalizedQuery)).length} {t('profileFamilyHistory.results')}
            </p>
          </div>
        )}

        {/* Active (selected) conditions */}
        {activeConditions.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {activeConditions.filter(c => !normalizedQuery || c.name.toLowerCase().includes(normalizedQuery)).map(c => renderConditionCard(c))}
          </div>
        )}

        {/* Custom conditions */}
        {otherConditions.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {otherConditions.map((c, i) => renderConditionCard(c, true, i))}
          </div>
        )}

        {/* Common items — only when NOT searching */}
        {!normalizedQuery && commonInactive.length > 0 && (
          <div className="space-y-1.5 mb-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-300 px-1 mb-1">{t('profileFamily.common')}</p>
            {commonInactive.map(c => renderConditionCard(c))}
          </div>
        )}

        <button onClick={() => setShowAddModal(true)}
          className="w-full py-2.5 mt-3 border-[1.5px] border-dashed rounded-[14px] text-[13px] font-semibold flex items-center justify-center gap-2"
          style={{ borderColor: 'rgba(70,168,249,0.3)', color: '#46A8F9' }}>
          <Plus size={16} /> {t('profileFamilyHistory.addOtherCondition')}
        </button>
      </ProfileSubpageLayout>

      {showAddModal && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[16px] font-bold" style={{ color: '#15354F' }}>{t('profileFamily.addCondition')}</h3>
              <button onClick={() => setShowAddModal(false)}><X className="text-gray-400" size={22} /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-[12px] text-gray-500 mb-1">{t('profileFamily.conditionName')}</label>
                <input type="text" value={newCondition} onChange={(e) => setNewCondition(e.target.value)} placeholder={t('profileFamilyHistory.conditionPlaceholder')}
                  className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9]" style={{ color: '#15354F' }} autoFocus /></div>
              <button onClick={handleAddCondition} disabled={!newCondition.trim()}
                className={`w-full py-3 rounded-2xl font-bold text-[14px] ${newCondition.trim() ? 'text-white' : 'bg-gray-200 text-gray-400'}`}
                style={newCondition.trim() ? { background: '#15354F' } : {}}>{t('profileFamily.add')}</button>
            </div>
          </div>
        </div>
        </OverlayPortal>
      )}
    </>
  )
}
